import type { ChangeEventHandler } from "react";

import type { TInputProps } from "@/ui/components/form/Input";
import type { TPasswordProps } from "@/ui/components/form/Password";
import {
  LOGIN_FIELD_CLASS,
  type TLoginForgotPasswordProps,
  type TLoginInputProps,
  type TLoginSubmitProps,
  type TLoginWithPasswordMessages,
  type TLoginWithPasswordTranslate,
} from "@/ui/components/sections/authentication/forms/Login/Login.config";
import { cn } from "@/ui/lib/utils";

export const getLoginWithPasswordMessages = (
  t: TLoginWithPasswordTranslate,
): TLoginWithPasswordMessages => ({
  emailLabel: t("emailLabel"),
  emailPlaceholder: t("emailPlaceholder"),
  passwordLabel: t("passwordLabel"),
  passwordPlaceholder: t("passwordPlaceholder"),
  forgotPasswordLabel: t("forgotPasswordLabel"),
  submitLabel: t("submitLabel"),
});

export const getLoginEmailProps = (
  email: TLoginInputProps,
  messages: TLoginWithPasswordMessages,
): TInputProps => {
  const {
    label,
    onValueChange,
    placeholder,
    ...props
  } = email;

  const onChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    onValueChange(event.target.value);
  };

  return {
    ...props,
    label: label ?? messages.emailLabel,
    name: "email",
    type: "email",
    autoComplete: email.autoComplete ?? "email",
    placeholder: placeholder ?? messages.emailPlaceholder,
    className: cn(LOGIN_FIELD_CLASS, props.className),
    onChange,
  };
};

export const getLoginPasswordProps = (
  password: TLoginInputProps,
  messages: TLoginWithPasswordMessages,
): TPasswordProps => {
  const {
    label,
    onValueChange,
    placeholder,
    ...props
  } = password;

  const onChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    onValueChange(event.target.value);
  };

  return {
    ...props,
    label: label ?? messages.passwordLabel,
    name: "password",
    autoComplete: password.autoComplete ?? "current-password",
    placeholder: placeholder ?? messages.passwordPlaceholder,
    className: cn(LOGIN_FIELD_CLASS, props.className),
    onChange,
  };
};

export const getLoginForgotPasswordProps = (
  forgotPassword: TLoginForgotPasswordProps,
  messages: TLoginWithPasswordMessages,
) => ({
  href: forgotPassword.href,
  label: forgotPassword.label ?? messages.forgotPasswordLabel,
});

export const getLoginSubmitProps = (
  submit: TLoginSubmitProps,
  messages: TLoginWithPasswordMessages,
) => {
  const {
    label,
    ...props
  } = submit;

  return {
    label: label ?? messages.submitLabel,
    props,
  };
};
